import { Action, Module, Mutation } from 'vuex-module-decorators'
import BaseStore from '@/store/BaseStore'
import {
  CollectionListSearchConditionItem,
  CollectionListSearchTableItem,
  defaultCollectionListSearchConditionItem
} from '@/components/templates/collection-list'
import {
  CollectionList,
  collectionListApi,
  CollectionListDetail,
  GetCollectionListDetailRequest,
  SearchCollectionListRequest
} from '@/api'
import { defaultSortColumn, SortColumn } from '@/components/molecules/DataTable'
import { CollectionListStatus } from '@/types'

@Module({
  name: 'CollectionListStore',
  namespaced: true,
  stateFactory: true
})
export default class CollectionListStore extends BaseStore {
  // 検索条件、登録画面で入力値などを表示する際に使用する
  _searchCondition: CollectionListSearchConditionItem = defaultCollectionListSearchConditionItem()

  // 検索した条件、更新、登録、削除後、同検索条件で最新の情報を取得する際に使用する
  _searchedCondition: CollectionListSearchConditionItem = defaultCollectionListSearchConditionItem()

  // 常に画面側と同期している
  _searchResults: CollectionListSearchTableItem[] = []

  _sortColumn: SortColumn = defaultSortColumn()

  get searchCondition(): CollectionListSearchConditionItem {
    return this._searchCondition
  }

  get searchedCondition(): CollectionListSearchConditionItem {
    return this._searchedCondition
  }

  get searchResults(): CollectionListSearchTableItem[] {
    return this._searchResults
  }

  get sortColumn(): SortColumn {
    return this._sortColumn
  }

  @Mutation
  public setName(value: string) {
    this._searchCondition.name.input = value
  }

  @Mutation
  public setStatus(value: CollectionListStatus) {
    this._searchCondition.status.input = value
  }

  @Mutation
  public setSearchedCondition(value: CollectionListSearchConditionItem) {
    this._searchedCondition = value
  }

  @Mutation
  public initializeSearchCondition() {
    this._searchCondition = defaultCollectionListSearchConditionItem()
  }

  @Mutation
  public onSort(value: SortColumn) {
    this._sortColumn = value
  }

  @Mutation
  public sortAgain(): void {
    this._sortColumn = { ...this._sortColumn }
  }

  @Mutation
  public initializeSort(): void {
    this._sortColumn = defaultSortColumn()
  }

  @Mutation
  public setSearchResults(value: CollectionListSearchTableItem[]) {
    this._searchResults = value
  }

  @Mutation
  public selectSearchResults(index: number) {
    if (this._searchResults.length === 0) {
      return
    }

    this._searchResults.forEach(
      (item: CollectionListSearchTableItem, loopIndex: number) =>
        (item.isSelected = loopIndex === index)
    )
  }

  @Mutation
  public updateSearchResult(obj: {
    index: number
    item: CollectionListSearchTableItem
  }) {
    this._searchResults.splice(obj.index, 1, obj.item)
  }

  @Mutation
  public addSearchResult(item: CollectionListSearchTableItem) {
    this._searchResults.push(item)
  }

  @Mutation
  public deleteCollectionListItem(index: number) {
    this._searchResults.splice(index, 1)
  }

  @Mutation
  public reset() {
    this._searchCondition = defaultCollectionListSearchConditionItem()
    this._searchedCondition = defaultCollectionListSearchConditionItem()
    this._searchResults = []
    this._sortColumn = defaultSortColumn()
  }

  @Action({ rawError: true })
  public async searchCollectionList(): Promise<void> {
    await this.searchInterface({
      name: this.searchCondition.name.input,
      status: this.searchCondition.status.input
    })
  }

  @Action({ rawError: true })
  public async searchAllCollectionList(): Promise<void> {
    await this.searchInterface({
      name: '',
      status: CollectionListStatus.All
    })
  }

  @Action({ rawError: true })
  public async reacquisitionCollectionList(
    request: GetCollectionListDetailRequest
  ): Promise<void> {
    const response: CollectionListDetail = await collectionListApi.getCollectionListDetail(
      request
    )
    const existsSearchResults: boolean = this._searchResults.length > 0

    const targetIndex: number = this._searchResults.findIndex(
      searchResult => searchResult.id === response.id
    )

    this.updateSearchResult({
      index: existsSearchResults ? targetIndex : 0,
      item: {
        isSelected: true,
        id: response.id,
        name: response.name,
        nameKana: response.nameKana,
        registrationCount: response.bibliographies.length,
        releaseStartDate: response.releaseStartDate,
        releaseEndDate: response.releaseEndDate,
        status: response.status,
        unlimitedFlag: response.unlimitedFlag,
        priority: response.priority,
        revision: response.revision
      }
    })

    this.selectSearchResults(existsSearchResults ? targetIndex : 0)
    this.sortAgain()
  }

  @Action({ rawError: true })
  public async addNewCollectionList(
    request: GetCollectionListDetailRequest
  ): Promise<void> {
    const response: CollectionListDetail = await collectionListApi.getCollectionListDetail(
      request
    )

    this.addSearchResult({
      isSelected: true,
      id: response.id,
      name: response.name,
      nameKana: response.nameKana,
      registrationCount: response.bibliographies.length,
      releaseStartDate: response.releaseStartDate,
      releaseEndDate: response.releaseEndDate,
      status: response.status,
      unlimitedFlag: response.unlimitedFlag,
      priority: response.priority,
      revision: response.revision
    })

    this.selectSearchResults(this._searchResults.length - 1)
    this.sortAgain()
  }

  @Action({ rawError: true })
  private async searchInterface(
    request: SearchCollectionListRequest
  ): Promise<void> {
    const responses: CollectionList[] = await collectionListApi.searchCollectionList(
      request
    )

    this.setSearchResults(
      responses.map((response: CollectionList, index: number) => {
        return {
          isSelected: index === 0,
          ...response
        }
      })
    )

    this.setSearchedCondition({
      name: {
        input: request.name
      },
      status: {
        input: request.status
      }
    })
    this.initializeSort()
  }
}
